import { Tag, Table, Text } from '@bancobarigui/barihaus';
import { PictureOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { QuestionResponse } from '../../types/checklist-response.types';
import { S3Image } from '@/components/S3Image';
import { FileViewer } from '@/components/FileViewer';

interface AnswerDisplayProps {
  response: QuestionResponse;
  onEditPhoto?: (photo: any, questionId: string) => void;
}

export function AnswerDisplay({ response, onEditPhoto }: AnswerDisplayProps) {
  const { question, answer } = response;

  const renderAnswer = () => {
    if ('isNotApplicable' in answer && answer.isNotApplicable) {
      return <Text type="secondary">Não se aplica</Text>;
    }

    switch (question.answerType) {
      case 'ShortAnswer':
      case 'LongAnswer':
        return (
          <div>
            <Text>{(answer as any).textValue || '-'}</Text>
            {(answer as any).notes && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Justificativa: {(answer as any).notes}
                </Text>
              </div>
            )}
          </div>
        );

      case 'YesNo':
      case 'RadioButton':
        return (
          <div>
            <Tag color={(answer as any).selectedOption === 'Sim' ? 'success' : 'default'}>
              {(answer as any).selectedOption}
            </Tag>
            {(answer as any).notes && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Justificativa: {(answer as any).notes}
                </Text>
              </div>
            )}
          </div>
        );

      case 'Checkbox':
        return (
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(answer as any).selectedOptions?.map((option: string, idx: number) => (
                <Tag key={idx} color="blue">
                  {option}
                </Tag>
              )) || <Text type="secondary">Nenhuma opção selecionada</Text>}
            </div>
            {(answer as any).notes && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Justificativa: {(answer as any).notes}
                </Text>
              </div>
            )}
          </div>
        );

      case 'Date':
        const dateValue = (answer as any).dateValue;
        return (
          <Text>
            {dateValue ? new Date(dateValue).toLocaleDateString('pt-BR') : '-'}
          </Text>
        );

      case 'NumericInteger':
      case 'NumericDecimal':
        return (
          <div>
            <Text>{(answer as any).numericValue?.toLocaleString('pt-BR') || '-'}</Text>
            {(answer as any).notes && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Justificativa: {(answer as any).notes}
                </Text>
              </div>
            )}
          </div>
        );

      case 'Currency':
        return (
          <Text>
            {(answer as any).numericValue
              ? new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format((answer as any).numericValue)
              : '-'}
          </Text>
        );

      case 'Table':
        const tableData = (answer as any).tableData || [];
        if (tableData.length === 0) {
          return <Text type="secondary">Nenhum dado na tabela</Text>;
        }

        // Extrair colunas do primeiro registro
        const columns = Object.keys(tableData[0]?.columns || {}).map((key) => ({
          title: key,
          dataIndex: key,
          key,
          render: (_: any, record: any) => record.columns[key],
        }));

        return (
          <Table
            dataSource={tableData}
            columns={columns}
            rowKey="rowNumber"
            size="small"
            pagination={false}
            bordered
          />
        );

      case 'Photo':
        const photos = (answer as any).photos || [];
        if (photos.length === 0) {
          return <Text type="secondary">Nenhuma foto anexada</Text>;
        }

        return (
          <div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
              {photos.map((photo: any, idx: number) => (
                <div key={idx} style={{ textAlign: 'center', position: 'relative' }}>
                  {onEditPhoto && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => onEditPhoto(photo, question.id)}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      }}
                    />
                  )}
                  <S3Image
                    src={photo.fileUrl}
                    documentId={photo.fileHash}
                    alt={photo.fileName}
                    width={120}
                    height={120}
                  />
                  {photo.caption && (
                    <Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 11, marginTop: 4, maxWidth: 120 }}
                    >
                      {photo.caption}
                    </Text>
                  )}
                  <Text
                    type="secondary"
                    style={{ display: 'block', fontSize: 10, marginTop: 4, maxWidth: 120, wordBreak: 'break-all' }}
                  >
                    {photo.fileName}
                  </Text>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PictureOutlined /> {photos.length} {photos.length === 1 ? 'foto' : 'fotos'}
              </Text>
            </div>
          </div>
        );

      case 'File':
        const files = (answer as any).files || [];
        if (files.length === 0) {
          return <Text type="secondary">Nenhum arquivo anexado</Text>;
        }

        return (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {files.map((file: any, idx: number) => (
                <FileViewer
                  key={idx}
                  fileName={file.fileName}
                  fileUrl={file.fileUrl}
                  documentId={file.fileHash}
                  fileSizeBytes={file.fileSizeBytes}
                />
              ))}
            </div>
          </div>
        );

      default:
        return <Text type="secondary">Tipo de resposta não suportado</Text>;
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 6 }}>
        <Text strong style={{ fontSize: 13 }}>
          {question.title}
        </Text>
      </div>
      <div>{renderAnswer()}</div>
    </div>
  );
}
